import { isBayesian } from '@gonfalon/experiments/isBayesian';
import { isGoodSignificant } from '@gonfalon/experiments/isSignificant';
import type {
  ExperimentAnalysis,
  Iteration,
  Layer,
  LayerSnapshot,
  Treatment,
  TreatmentAnalysis,
} from '@gonfalon/experiments/types';

// returns the primary metric key for an iteration
// if the primary metric is a group, returns the key for the last metric in the group
// This is a temporary solution so features such as the Traffic Table, SRM alerts, and Sample Size Estimator
// can function by fetching results for a single metric.
export function mustGetPrimaryMetricKey(
  iteration: Pick<Iteration, 'primarySingleMetric' | 'primaryFunnel'> | undefined | null,
) {
  return iteration?.primarySingleMetric?.key ?? iteration?.primaryFunnel?.metrics?.slice(-1)?.[0]?.key ?? '';
}

/**
 * Orders treatments the way the results table renders them (see `extractTreatmentAnalyses`):
 * the baseline treatment(s) first, then the remaining treatments in their original order.
 * Centralizing this keeps the results table and the historical-results charts consistent.
 */
export function orderTreatmentsForDisplay<T extends { baseline?: boolean }>(treatments: readonly T[] = []): T[] {
  return treatments.reduce<T[]>(
    (acc, treatment) => (treatment.baseline ? [treatment, ...acc] : [...acc, treatment]),
    [],
  );
}

/**
 * Calculates the Bayesian winner treatment based on analysis data.
 * A treatment is considered a winner if it has the highest pBBest value
 * and that value exceeds the pBBaselineThreshold.
 */
export function calculateBayesianWinner(
  experimentMethodology: string | undefined,
  iteration: Iteration | undefined,
  analysisData: ExperimentAnalysis | undefined,
  isLoading: boolean,
  isError: boolean,
): Treatment | undefined {
  if (!isBayesian(experimentMethodology) || !analysisData || isError || isLoading) {
    return undefined;
  }

  const leadingTreatmentIndex = analysisData?.leadingTreatmentIndex;
  const leadingTreatmentAnalysis =
    leadingTreatmentIndex !== undefined ? analysisData?.treatments[leadingTreatmentIndex] : undefined;

  if (!leadingTreatmentAnalysis) {
    return undefined;
  }

  // Leading treatment has highest pBBest; if that pBBest is higher than the threshold, return it as the winner
  if (
    leadingTreatmentAnalysis.methodology === 'bayesian' &&
    leadingTreatmentAnalysis.modelAnalysis?.pBBest !== undefined &&
    leadingTreatmentAnalysis.modelAnalysis?.pBBaselineThreshold !== undefined &&
    leadingTreatmentAnalysis.modelAnalysis?.pBBest > leadingTreatmentAnalysis.modelAnalysis?.pBBaselineThreshold / 100
  ) {
    return iteration?.treatments?.find((treatment) => treatment._id === leadingTreatmentAnalysis.treatmentId);
  }

  return undefined;
}

/**
 * Returns the treatment with the highest PBBest value,
 * the best performing treatment when there is no bayesian winner.
 */
export function getLeadingBayesianTreatment(
  experimentAnalysis: ExperimentAnalysis | undefined,
  iteration: Iteration | undefined,
) {
  // TODO use leadingTreatmentIndex here after we change that logic to match what we have here EXPT-3263
  let maxPBBest = 0;
  let leadingTreatmentAnalysis: TreatmentAnalysis | undefined;
  experimentAnalysis?.treatments.forEach((treatmentAnalysis) => {
    if (
      treatmentAnalysis.methodology === 'bayesian' &&
      treatmentAnalysis.modelAnalysis?.pBBest &&
      treatmentAnalysis.modelAnalysis.pBBest > maxPBBest
    ) {
      maxPBBest = treatmentAnalysis.modelAnalysis.pBBest;
      leadingTreatmentAnalysis = treatmentAnalysis;
    }
  });

  return iteration?.treatments?.find((treatment) => treatment._id === leadingTreatmentAnalysis?.treatmentId);
}

export function getFrequentistSignificantTreatments(
  experimentAnalysis: ExperimentAnalysis | undefined,
  iteration: Iteration | undefined,
) {
  const significantTreatmentAnalyses =
    experimentAnalysis?.treatments.filter((a) => isGoodSignificant(a, experimentAnalysis.metric?.successCriteria)) ??
    [];
  if (significantTreatmentAnalyses.length) {
    return significantTreatmentAnalyses
      .map((treatmentAnalysis) =>
        iteration?.treatments?.find((treatment) => treatment._id === treatmentAnalysis.treatmentId),
      )
      .filter((val) => val !== undefined)
      .toSorted((t) => (!!t.baseline ? -1 : 1));
  }
  return [];
}

export function getLayerReservation(layer: Layer, experimentKey: string, environmentKey: string): number {
  const reservations = (layer.environments && layer.environments[environmentKey]?.reservations) ?? [];
  return reservations.find((res) => res.experimentKey === experimentKey)?.reservationPercent ?? 0;
}

export function layerToSnapshot(layer: Layer, environmentKey: string, experimentKey: string): LayerSnapshot {
  const reservations = (layer.environments && layer.environments[environmentKey]?.reservations) ?? [];
  return reservations.reduce<LayerSnapshot>(
    (acc, r) => {
      if (r.experimentKey === experimentKey) {
        return {
          ...acc,
          reservationPercent: r.reservationPercent,
        };
      }
      return {
        ...acc,
        otherReservationPercent: acc.otherReservationPercent + r.reservationPercent,
      };
    },
    { name: layer.name, key: layer.key, reservationPercent: 0, otherReservationPercent: 0 },
  );
}
